import React, { CSSProperties } from 'react'
import classNames from 'classnames'
import { Icon } from '@slite/icons'

import {
  ButtonVariant,
  buttonSizesClasses,
  buttonVariantsClasses,
} from './constants'
import {
  CustomNoteIcon,
  CustomNoteIconProps,
} from '../CustomNoteIcon/CustomNoteIcon'

const BASE_CLASSES =
  'pointer transition font-size-large flexbox flexbox-align-center flexbox-justify-center font-family-system flexbox-no-shrink extra-white-outline-on-focus'

const DISABLED_CLASSES = 'cursor-forbidden'

const buttonSizesValues = {
  smaller: 24,
  small: 28,
  medium: 36,
  large: 44,
} as const

type BaseButtonContentProps = {
  icon: BaseButtonProps['icon']
  iconShape?: BaseButtonProps['iconShape']
  iconColor?: BaseButtonProps['iconColor']
  iconVariant?: BaseButtonProps['iconVariant']
  content: React.ReactNode
  size: BaseButtonProps['size']
  iconClassName?: string
  iconPosition?: 'left' | 'right'
  iconSize?: BaseButtonProps['size']
}

const ICON_SIZE = {
  smaller: 12,
  small: 12,
  medium: 16,
  large: 16,
}

const ICON_MARGIN_CLASS = {
  smaller: 4,
  small: 4,
  medium: 8,
  large: 8,
}

const getIconMarginClass = ({
  size = 'medium',
  iconPosition = 'left',
}: Pick<BaseButtonContentProps, 'iconPosition' | 'size'>) => {
  const marginSide = iconPosition === 'left' ? 'right' : 'left'
  return `margin-${marginSide}-${ICON_MARGIN_CLASS[size]}`
}

export const BaseButtonContent = ({
  icon: ButtonIcon,
  iconShape,
  iconColor,
  iconVariant,
  content,
  iconClassName,
  iconPosition = 'left',
  iconSize,
  size = 'medium',
}: BaseButtonContentProps) => {
  const computedIconSize = ICON_SIZE[iconSize ?? size]
  const iconMargin =
    content == null
      ? ''
      : getIconMarginClass({
          size,
          iconPosition,
        })

  const renderButtonIconComponent = () => {
    // If specified, use emoji icon as first choice
    if (iconShape) {
      return (
        <CustomNoteIcon
          className={classNames('flexbox-no-shrink', iconMargin, iconClassName)}
          iconShape={iconShape}
          iconColor={iconColor}
          iconVariant={iconVariant}
          size={computedIconSize}
        />
      )
    }

    // If no emoji icon is specified, use static icon or no icon

    if (!ButtonIcon) {
      return null
    }

    return (
      <ButtonIcon
        size={computedIconSize}
        style={{ width: computedIconSize, height: computedIconSize }}
        className={classNames('flexbox-no-shrink', iconMargin, iconClassName)}
      />
    )
  }

  return (
    <>
      {iconPosition === 'left' && renderButtonIconComponent()}
      {content}
      {iconPosition === 'right' && renderButtonIconComponent()}
    </>
  )
}

export type BaseButtonProps = {
  children: (className: string, style: React.CSSProperties) => React.ReactNode
  size?: 'smaller' | 'small' | 'medium' | 'large'
  variant?: ButtonVariant
  disabled?: boolean
  className?: string
  style?: CSSProperties
  icon?: Icon | null
  iconShape?: CustomNoteIconProps['iconShape']
  iconColor?: CustomNoteIconProps['iconColor']
  iconVariant?: CustomNoteIconProps['iconVariant']
  isActive?: boolean
  iconClassName?: string
  iconPosition?: 'left' | 'right'
  iconSize?: BaseButtonProps['size']
  rounded?: boolean
}

export const BaseButton = ({
  variant = 'primary',
  size = 'large',
  disabled = false,
  isActive = false,
  className,
  children: renderChildren,
  rounded = false,
}: BaseButtonProps) => {
  const _className = classNames(
    BASE_CLASSES,
    buttonVariantsClasses[variant]?.base,
    buttonSizesClasses[size],
    !rounded && (size === 'smaller' ? 'radius-normal' : 'radius-large'),
    {
      // default
      pointer: !disabled,
      [buttonVariantsClasses[variant]?.default || '']: !disabled && !isActive,

      // disabled
      [DISABLED_CLASSES]: disabled,
      [buttonVariantsClasses[variant]?.disabled || '']: disabled && !isActive,
    },
    {
      // isActive
      [buttonVariantsClasses[variant]?.isActive || '']: isActive && !disabled,
    },
    className
  )
  const roundedStyle = rounded
    ? { borderRadius: buttonSizesValues[size] / 2 }
    : null
  const _style = { minHeight: buttonSizesValues[size], ...roundedStyle }

  return <>{renderChildren(_className, _style)}</>
}
